import { useEffect, useState } from "react";
import {
  Aperture,
  ArrowDownRight,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Camera,
  Check,
  ChevronDown,
  CirclePlay,
  Fingerprint,
  Heart,
  Images,
  LoaderCircle,
  LockKeyhole,
  Menu,
  ScanFace,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { requestOtp, verifyOtp } from "./api";
import { captureOtpFill, showDevOtpFill } from "./otp-fill";
import {
  getLoginIntent,
  getPanelUrls,
  loginHref,
  photographerApplicationHref,
  resolvePostLogin,
  resolveServerPostLogin,
  withAuthHandoff,
} from "./public-auth-routing";
import {
  describeLoginError,
  otpTiming,
  secondsRemaining,
} from "./public-login-state";
import "./public-experience.css";

const imagery = {
  hero: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1500&q=88",
  login:
    "https://images.unsplash.com/photo-1606800052052-a08af7148866?auto=format&fit=crop&w=1400&q=88",
  momentOne:
    "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=240&q=80",
  momentTwo:
    "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=240&q=80",
};

const features = [
  {
    icon: Camera,
    intent: "photographer",
    title: "Be a Partner Photographer",
    description:
      "Join the PhotoX photographer network, create your profile and manage professional event assignments.",
    tag: "JOIN OUR NETWORK",
  },
  {
    icon: Images,
    intent: "client",
    title: "Need a Photographer",
    href: "/find-photographer",
    description:
      "Tell us about your event. We'll help you find an available photographer and review a tailored quote.",
    tag: "PLAN YOUR EVENT",
  },
  {
    icon: ScanFace,
    intent: "guest",
    title: "For guests",
    description:
      "Scan the event QR, open the private gallery and use face search to find your moments.",
    tag: "FIND YOURSELF",
  },
];

const steps = [
  {
    number: "01",
    title: "The event takes shape",
    text: "A photographer prepares the event and brings each professional photograph into its private gallery.",
  },
  {
    number: "02",
    title: "Guests scan the invitation",
    text: "The event QR opens the right gallery and keeps that invitation connected through secure email sign-in.",
  },
  {
    number: "03",
    title: "Everyone finds their view",
    text: "Clients manage the event, photographers deliver it, and guests can search for the photos they appear in.",
  },
];

const intentCopy = {
  client: {
    eyebrow: "CLIENT ACCESS",
    title: "Your event, beautifully in view.",
    description:
      "Use the email connected to your PhotoX event to open the Client workspace.",
  },
  photographer: {
    eyebrow: "PHOTOGRAPHER ACCESS",
    title: "Your work deserves a focused space.",
    description:
      "Sign in to manage your workspace or begin your PhotoX Photographer application.",
  },
  guest: {
    eyebrow: "GUEST ACCESS",
    title: "Find the moments you were part of.",
    description:
      "Sign in to revisit an event gallery and use private, event-only face search.",
  },
  default: {
    eyebrow: "SECURE ACCESS",
    title: "Welcome to your moments.",
    description:
      "One verified email opens the PhotoX workspace connected to your account.",
  },
};

function imageFallback(event) {
  const image = event.currentTarget;
  if (image.dataset.fallbackApplied) return;
  image.dataset.fallbackApplied = "true";
  image.src = image.dataset.fallback;
}

function Brand({ inverted = false }) {
  return (
    <a
      className={`px-site-brand${inverted ? " px-site-brand--inverse" : ""}`}
      href="/"
      aria-label="PhotoX homepage"
    >
      <span className="px-site-brand-mark">
        <Aperture size={21} strokeWidth={2.4} />
      </span>
      <span>
        photo<span className="px-site-brand-x">x</span>
        <small>STORIES, FOUND.</small>
      </span>
    </a>
  );
}

function Header({ minimal = false }) {
  const [open, setOpen] = useState(false);
  return (
    <header
      className={`px-site-header${minimal ? " px-site-header--minimal" : ""}`}
    >
      <div className="px-site-container px-site-header-inner">
        <Brand />
        {!minimal && (
          <>
            <nav
              className={`px-site-nav${open ? " is-open" : ""}`}
              aria-label="Main navigation"
            >
              <a href="/#how-it-works" onClick={() => setOpen(false)}>
                How it works
              </a>
              <a href="/#experience" onClick={() => setOpen(false)}>
                The experience
              </a>
              <a href="/#for-everyone" onClick={() => setOpen(false)}>
                For everyone
              </a>
              <a href="/#questions" onClick={() => setOpen(false)}>
                FAQs
              </a>
              <a className="px-site-nav-mobile-login" href="/login">
                Sign in <ArrowRight size={16} />
              </a>
            </nav>
            <div className="px-site-header-actions">
              <a className="px-site-header-signin" href="/login">
                Sign in
              </a>
              <a
                className="px-site-button px-site-button--small"
                href="/find-photographer"
              >
                Need a Photographer <ArrowUpRight size={16} />
              </a>
            </div>
            <button
              className="px-site-menu-toggle"
              type="button"
              aria-expanded={open}
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen(!open)}
            >
              {open ? <X /> : <Menu />}
            </button>
          </>
        )}
        {minimal && (
          <a className="px-site-header-back" href="/">
            <ArrowLeft size={16} /> Back to website
          </a>
        )}
      </div>
    </header>
  );
}

function StoryVisual() {
  return (
    <div className="px-story-visual" aria-label="PhotoX event gallery preview">
      <div className="px-story-glow" aria-hidden="true" />
      <div className="px-story-frame">
        <img
          className="px-story-image"
          src={imagery.hero}
          data-fallback="/assets/photo-x-event-art.svg"
          onError={imageFallback}
          alt="A newly married couple celebrating beneath warm lights"
          fetchpriority="high"
        />
        <div className="px-story-photo-label">
          <span className="px-story-label-dot" /> LIVE MOMENTS
          <span className="px-story-label-id">PX / 001</span>
        </div>
        <div className="px-story-frame-bottom">
          <div>
            <span>THE MEMORY COLLECTION</span>
            <strong>Every frame has a story.</strong>
          </div>
          <div className="px-story-frame-bottom-icon">
            <ArrowUpRight size={22} />
          </div>
        </div>
      </div>
      <div className="px-story-floating px-story-floating--top">
        <span className="px-story-floating-icon">
          <ScanFace size={20} />
        </span>
        <div>
          <strong>Find your moments</strong>
          <small>Private event photo discovery</small>
        </div>
        <span className="px-story-tick">
          <Check size={13} />
        </span>
      </div>
      <div className="px-story-floating px-story-floating--bottom">
        <span className="px-story-gallery-dots">
          <img
            src={imagery.momentOne}
            data-fallback="/assets/photo-x-moment-one.svg"
            onError={imageFallback}
            alt=""
            loading="lazy"
          />
          <img
            src={imagery.momentTwo}
            data-fallback="/assets/photo-x-moment-two.svg"
            onError={imageFallback}
            alt=""
            loading="lazy"
          />
          <img
            src={imagery.hero}
            data-fallback="/assets/photo-x-event-art.svg"
            onError={imageFallback}
            alt=""
            loading="lazy"
          />
        </span>
        <div>
          <strong>A gallery for everyone</strong>
          <small>One event. Countless memories.</small>
        </div>
      </div>
      <div className="px-story-orbit px-story-orbit--one" />
      <div className="px-story-orbit px-story-orbit--two" />
      <a
        className="px-site-photo-credit"
        href="https://unsplash.com/?utm_source=photox&utm_medium=referral"
        target="_blank"
        rel="noreferrer"
      >
        Photography via Unsplash
      </a>
    </div>
  );
}

export function HomePage() {
  const partnerApplicationUrl = photographerApplicationHref();

  return (
    <div className="px-public-page">
      <Header />
      <main>
        <section className="px-site-hero" id="top">
          <div className="px-site-container px-site-hero-grid">
            <div className="px-site-hero-copy">
              <div className="px-site-kicker">
                <span className="px-site-kicker-star">
                  <Sparkles size={13} />
                </span>
                THE NEW WAY TO KEEP EVERY MOMENT
                <span className="px-site-kicker-dash" />
              </div>
              <h1>
                Moments fade.
                <br />
                <em>Stories</em> stay.
              </h1>
              <p className="px-site-hero-summary">
                Beautifully organized event galleries, effortless sharing and
                private photo discovery—for the people who make the moments and
                the people who live them.
              </p>
              <div className="px-site-hero-actions">
                <a
                  className="px-site-button px-site-button--large"
                  href="/find-photographer"
                >
                  Need a Photographer <ArrowUpRight size={19} />
                </a>
                <a
                  className="px-site-button px-site-button--large px-site-button--outline"
                  href={partnerApplicationUrl}
                >
                  Be a Partner Photographer <ArrowUpRight size={19} />
                </a>
              </div>
              <div className="px-site-hero-subactions">
                <a href={loginHref("client")}>
                  Existing client sign in <ArrowRight size={15} />
                </a>
                <a href={loginHref("guest")}>
                  Guest gallery & face search <ArrowRight size={15} />
                </a>
                <a href="#how-it-works">
                  <CirclePlay size={17} /> See how it works
                </a>
              </div>
              <div className="px-site-guest-cue">
                <ScanFace size={18} />
                <span>
                  <strong>Attending an event?</strong> Your event QR is the
                  fastest and safest way in.
                </span>
              </div>
            </div>
            <StoryVisual />
          </div>
          <div className="px-site-hero-footer px-site-container">
            <span>
              <span className="px-site-hero-footer-rule" /> CRAFTED AROUND YOUR
              MOMENTS
            </span>
            <a href="#for-everyone">
              DISCOVER THE EXPERIENCE <ArrowDownRight size={18} />
            </a>
          </div>
        </section>

        <section className="px-site-intro px-site-section" id="experience">
          <div className="px-site-container">
            <div className="px-site-section-heading">
              <span className="px-site-overline">
                A DIFFERENT WAY TO REMEMBER
              </span>
              <h2>
                The event ends.
                <br />
                <span>The experience doesn’t.</span>
              </h2>
              <p>
                A dedicated space for every event—from the complete professional
                gallery to the personal moments each guest wants to find.
              </p>
            </div>
            <div className="px-site-feature-strip">
              <div>
                <span className="px-feature-icon">
                  <Images size={22} />
                </span>
                <strong>Thoughtful galleries</strong>
                <p>
                  Photos organized around the people and story of one event.
                </p>
              </div>
              <div>
                <span className="px-feature-icon">
                  <ScanFace size={22} />
                </span>
                <strong>Event-only face search</strong>
                <p>
                  Use a consented selfie to discover possible matches from an
                  authorized gallery.
                </p>
              </div>
              <div>
                <span className="px-feature-icon">
                  <ShieldCheck size={22} />
                </span>
                <strong>Privacy comes first</strong>
                <p>
                  Guest contributions stay private unless their owner chooses to
                  share them.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-site-roles px-site-section" id="for-everyone">
          <div className="px-site-container">
            <div className="px-site-roles-top">
              <div>
                <span className="px-site-overline px-site-overline--light">
                  CHOOSE YOUR ENTRANCE
                </span>
                <h2>
                  Your role. <em>Your world.</em>
                </h2>
              </div>
              <p>
                Request a photographer, apply to join our partner network, or
                open your guest gallery. Existing accounts use secure email
                sign-in.
              </p>
            </div>
            <div className="px-site-role-grid">
              {features.map(
                (
                  { icon: Icon, intent, href, title, description, tag },
                  index,
                ) => (
                  <a
                    href={
                      href ||
                      (intent === "photographer"
                        ? partnerApplicationUrl
                        : loginHref(intent))
                    }
                    className="px-site-role-card"
                    key={title}
                  >
                    <div className="px-site-role-number">0{index + 1} / 03</div>
                    <div className="px-site-role-symbol">
                      <Icon size={30} strokeWidth={1.6} />
                    </div>
                    <span className="px-site-role-tag">{tag}</span>
                    <h3>{title}</h3>
                    <p>{description}</p>
                    <span
                      className="px-site-role-arrow"
                      aria-label={`Explore ${title}`}
                    >
                      <ArrowUpRight size={19} />
                    </span>
                  </a>
                ),
              )}
            </div>
            <p className="px-site-roles-note">
              <LockKeyhole size={15} /> One secure email sign-in for Clients,
              Photographers and Guests. Admin access remains separate.
            </p>
          </div>
        </section>

        <section className="px-site-steps px-site-section" id="how-it-works">
          <div className="px-site-container px-site-steps-grid">
            <div className="px-site-steps-heading">
              <span className="px-site-overline">SIMPLE BY DESIGN</span>
              <h2>
                From camera
                <br />
                to <em>connection.</em>
              </h2>
              <p>
                Less time searching through links and folders. More time with
                the photographs that matter.
              </p>
              <a className="px-site-underline-link" href={loginHref("guest")}>
                Open guest access <ArrowUpRight size={17} />
              </a>
            </div>
            <div className="px-site-steps-list">
              {steps.map(({ number, title, text }) => (
                <div className="px-site-step" key={number}>
                  <span>{number}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                  <ArrowUpRight size={21} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-site-quote-section">
          <div className="px-site-container">
            <div className="px-site-quote-inner">
              <div className="px-site-quote-symbol">
                <Heart size={23} />
              </div>
              <p>“Some photos are too special to get lost in a folder.”</p>
              <span>MAKE ROOM FOR THE MOMENTS THAT MATTER.</span>
            </div>
          </div>
        </section>

        <section className="px-site-faq px-site-section" id="questions">
          <div className="px-site-container px-site-faq-grid">
            <div>
              <span className="px-site-overline">GOOD TO KNOW</span>
              <h2>
                A few things
                <br />
                you might ask.
              </h2>
              <p>Simple answers to help you choose the right entrance.</p>
            </div>
            <div className="px-site-faq-list">
              <details>
                <summary>
                  Do I need a different login for each role?
                  <ChevronDown size={19} />
                </summary>
                <p>
                  No. Clients, Photographers and Guests use the same email OTP
                  screen. After verification, the roles held by the account
                  determine the available workspace.
                </p>
              </details>
              <details>
                <summary>
                  How do guests access an event?
                  <ChevronDown size={19} />
                </summary>
                <p>
                  Most Guests scan their event QR, which preserves the correct
                  event through sign-in. Returning Guests can use Guest access
                  to revisit authorized galleries and face search.
                </p>
              </details>
              <details>
                <summary>
                  What if I am both a Client and Photographer?
                  <ChevronDown size={19} />
                </summary>
                <p>
                  After verification, PhotoX shows a workspace chooser so you
                  can open the right view without creating another account.
                </p>
              </details>
              <details>
                <summary>
                  Are my uploaded photos private?
                  <ChevronDown size={19} />
                </summary>
                <p>
                  Guest uploads are private by default. They appear in the
                  shared event gallery only when the Guest chooses to share
                  them.
                </p>
              </details>
            </div>
          </div>
        </section>

        <section className="px-site-bottom-cta">
          <div className="px-site-container">
            <div className="px-site-bottom-cta-inner">
              <span className="px-site-overline px-site-overline--light">
                KEEP THE STORY GOING
              </span>
              <h2>
                The best moments
                <br />
                deserve to be <em>found.</em>
              </h2>
              <div>
                <p>
                  Your photographs. Your people. Your memories—all in one
                  considered place.
                </p>
                <a
                  className="px-site-button px-site-button--light px-site-button--large"
                  href="/login"
                >
                  Sign in to PhotoX <ArrowUpRight size={20} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer partnerApplicationUrl={partnerApplicationUrl} />
    </div>
  );
}

function Footer({ partnerApplicationUrl = photographerApplicationHref() }) {
  return (
    <footer className="px-site-footer">
      <div className="px-site-container px-site-footer-top">
        <Brand />
        <p>
          Moments worth finding.
          <br />
          Stories worth keeping.
        </p>
        <div>
          <strong>EXPLORE</strong>
          <a href="/#how-it-works">How it works</a>
          <a href="/#for-everyone">Who it’s for</a>
          <a href="/login">Sign in</a>
        </div>
        <div>
          <strong>YOUR SPACE</strong>
          <a href="/find-photographer">Need a Photographer</a>
          <a href={partnerApplicationUrl}>Be a Partner Photographer</a>
          <a href={loginHref("guest")}>Guest gallery</a>
        </div>
      </div>
      <div className="px-site-container px-site-footer-bottom">
        <span>© {new Date().getFullYear()} PhotoX. Made for moments.</span>
        <span>
          Designed with care <span aria-hidden="true">✦</span>
        </span>
      </div>
    </footer>
  );
}

function WorkspaceChoices({ result }) {
  if (!result) return null;
  return (
    <div className="px-site-login-result" role="status">
      <BadgeCheck size={21} />
      <div>
        <strong>
          {result.kind === "choose"
            ? "Choose your workspace"
            : result.kind === "no-invitation"
              ? "No invitation found"
              : "Account verified"}
        </strong>
        {result.message && <p>{result.message}</p>}
        {result.kind === "choose" && (
          <p>This account has more than one PhotoX role.</p>
        )}
        <div className="px-site-workspace-options">
          {result.options.map((option) => (
            <a
              key={option.id}
              href={withAuthHandoff(option.url, result.handoff, option.id)}
            >
              {option.label}
              <ArrowUpRight size={15} />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export function UnifiedLoginPage() {
  const [otpFill, setOtpFill] = useState(() => captureOtpFill("user"));
  const [intent] = useState(() => getLoginIntent());
  const [email, setEmail] = useState(otpFill?.email || "");
  const [otpEmail, setOtpEmail] = useState(otpFill?.email || "");
  const [code, setCode] = useState(otpFill?.code || "");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [notice, setNotice] = useState("");
  const [timing, setTiming] = useState({
    resendAt: 0,
    expiresAt: otpFill?.expiresAt || 0,
  });
  const [now, setNow] = useState(() => Date.now());
  const [routeResult, setRouteResult] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const copy = intentCopy[intent] || intentCopy.default;
  const awaitingCode = Boolean(otpEmail);
  const resendIn = secondsRemaining(timing.resendAt, now);
  const expiresIn = secondsRemaining(timing.expiresAt, now);
  const codeExpired = awaitingCode && timing.expiresAt > 0 && expiresIn === 0;
  const displayedError =
    authError ||
    (codeExpired
      ? describeLoginError({
          code: "OTP_INVALID",
          message: "The verification code expired.",
        })
      : null);

  useEffect(() => {
    function handleOtpHash() {
      const fill = captureOtpFill("user");
      if (fill) {
        setOtpFill(fill);
        setOtpEmail(fill.email);
        setCode(fill.code);
        if (fill.expiresAt) {
          setTiming((current) => ({
            ...current,
            expiresAt: fill.expiresAt,
          }));
        }
      }
    }
    window.addEventListener("hashchange", handleOtpHash);
    window.addEventListener("focus", handleOtpHash);
    return () => {
      window.removeEventListener("hashchange", handleOtpHash);
      window.removeEventListener("focus", handleOtpHash);
    };
  }, []);

  useEffect(() => {
    if (!awaitingCode || (!resendIn && !expiresIn)) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [awaitingCode, expiresIn, resendIn]);

  async function sendCode(event, isResend = false) {
    event?.preventDefault();
    setBusy(true);
    setAuthError(null);
    setNotice("");
    setRouteResult(null);
    setDevCode("");
    const targetEmail = (otpEmail || email).trim().toLowerCase();
    try {
      const result = await requestOtp(targetEmail);
      const requestedAt = Date.now();
      setOtpEmail(targetEmail);
      setTiming(otpTiming(result, requestedAt));
      setNow(requestedAt);
      setCode("");
      if (isResend)
        setNotice(
          "A new verification code was sent. Only the latest code will work.",
        );
      if (result?.devCode) setDevCode(result.devCode);
    } catch (requestError) {
      if (requestError.code === "OTP_RESEND_COOLDOWN") {
        const retryAfter = Number(requestError.details?.retryAfterSeconds) || 1;
        setTiming((current) => ({
          ...current,
          resendAt: Date.now() + retryAfter * 1000,
        }));
      }
      setAuthError(describeLoginError(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event) {
    event.preventDefault();
    if (codeExpired) return;
    setBusy(true);
    setAuthError(null);
    setNotice("");
    setRouteResult(null);
    try {
      const result = await verifyOtp(otpEmail, code, intent);
      const urls = getPanelUrls();
      const next = result.routing
        ? resolveServerPostLogin(result.routing, { urls })
        : resolvePostLogin(result.user, intent, { urls });
      if (next.kind === "redirect") {
        if (!next.destination?.url) {
          setRouteResult({
            kind: "unavailable",
            message:
              "This workspace address has not been configured for this deployment. Please contact PhotoX support.",
            options: [],
          });
          return;
        }
        window.location.assign(
          withAuthHandoff(
            next.destination.url,
            result.handoff,
            next.destination.id,
          ),
        );
        return;
      }
      setRouteResult({ ...next, handoff: result.handoff });
    } catch (requestError) {
      setAuthError(describeLoginError(requestError));
    } finally {
      setBusy(false);
    }
  }

  function resetEmail() {
    setOtpEmail("");
    setCode("");
    setDevCode("");
    setOtpFill(null);
    setAuthError(null);
    setNotice("");
    setTiming({ resendAt: 0, expiresAt: 0 });
    setRouteResult(null);
  }

  return (
    <div className="px-public-page px-site-login-page">
      <Header minimal />
      <main className="px-site-login-layout">
        <div className="px-site-login-art">
          <img
            className="px-site-login-photo"
            src={imagery.login}
            data-fallback="/assets/photo-x-event-art.svg"
            onError={imageFallback}
            alt="Hands joined during a wedding celebration"
          />
          <div className="px-site-login-art-shade" />
          <div className="px-site-login-art-inner">
            <div className="px-site-login-decoration">
              <Aperture size={30} />
              <span>ONE PLATFORM. EVERY PERSPECTIVE.</span>
            </div>
            <h2>
              Everything
              <br />
              beautiful begins
              <br />
              <em>with a moment.</em>
            </h2>
            <p>
              A single secure entrance to your events, galleries and photo
              memories.
            </p>
            <div className="px-site-login-art-tag">
              <span>
                <Fingerprint size={18} />
              </span>
              One sign-in. Your space.
            </div>
            <div className="px-site-login-art-bottom">
              <span>PHOTOGRAPHERS</span>
              <span className="px-site-art-dot" />
              <span>CLIENTS</span>
              <span className="px-site-art-dot" />
              <span>GUESTS</span>
            </div>
            <a
              className="px-site-login-credit"
              href="https://unsplash.com/?utm_source=photox&utm_medium=referral"
              target="_blank"
              rel="noreferrer"
            >
              Photography via Unsplash
            </a>
          </div>
        </div>

        <section
          className="px-site-login-content"
          aria-labelledby="px-login-title"
        >
          <div className="px-site-login-form-area">
            <div className="px-site-login-kicker">
              <span className="px-site-login-kicker-icon">
                <LockKeyhole size={15} />
              </span>
              {copy.eyebrow}
              <span className="px-site-login-live">LIVE OTP</span>
            </div>
            <h1 id="px-login-title">{copy.title}</h1>
            <p className="px-site-login-description">
              {awaitingCode
                ? `Enter the verification code sent to ${otpEmail}.`
                : copy.description}
            </p>

            {!awaitingCode ? (
              <form
                className="px-site-login-form"
                onSubmit={sendCode}
                aria-busy={busy}
              >
                <label htmlFor="px-shared-email">Email address</label>
                <div className="px-site-login-field">
                  <input
                    id="px-shared-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setAuthError(null);
                    }}
                    disabled={busy}
                    required
                  />
                  <span>@</span>
                </div>
                <button
                  className="px-site-button px-site-button--large px-site-button--full"
                  type="submit"
                  disabled={busy}
                >
                  {busy ? (
                    <LoaderCircle className="px-site-spin" size={19} />
                  ) : (
                    <ArrowRight size={19} />
                  )}
                  {busy ? "Sending secure code…" : "Continue with email"}
                </button>
              </form>
            ) : (
              <form
                className="px-site-login-form"
                onSubmit={verifyCode}
                aria-busy={busy}
              >
                <label htmlFor="px-shared-code">Verification code</label>
                <div className="px-site-login-field px-site-login-field--code">
                  <input
                    id="px-shared-code"
                    type="text"
                    name="code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    placeholder="000000"
                    value={code}
                    onChange={(event) => {
                      setCode(
                        event.target.value.replace(/\D/g, "").slice(0, 8),
                      );
                      setAuthError(null);
                    }}
                    minLength={6}
                    maxLength={8}
                    disabled={busy || codeExpired}
                    autoFocus
                    required
                  />
                  <span>
                    <Fingerprint size={19} />
                  </span>
                </div>
                {otpFill?.code && (
                  <button
                    className="px-site-dev-code"
                    type="button"
                    onClick={() => setCode(otpFill.code)}
                  >
                    Magic link code: <strong>{otpFill.code}</strong>{" "}
                    <span>Tap to fill</span>
                  </button>
                )}
                {showDevOtpFill(devCode) && !otpFill?.code && (
                  <button
                    className="px-site-dev-code"
                    type="button"
                    onClick={() => {
                      setCode(devCode);
                      setAuthError(null);
                    }}
                    title="Tap to fill the same OTP emailed by SES (testing only)"
                  >
                    <span>
                      Testing OTP: <strong>{devCode}</strong>
                    </span>
                    <span style={{ fontWeight: 800, color: "#2563eb" }}>
                      {code === devCode ? "✓ Filled" : "Tap to fill"}
                    </span>
                  </button>
                )}
                <div className="px-site-code-timing" aria-live="polite">
                  {codeExpired
                    ? "Code expired"
                    : `Code expires in ${Math.floor(expiresIn / 60)}:${String(expiresIn % 60).padStart(2, "0")}`}
                </div>
                <button
                  className="px-site-button px-site-button--large px-site-button--full"
                  type="submit"
                  disabled={busy || codeExpired || code.length < 6}
                >
                  {busy ? (
                    <LoaderCircle className="px-site-spin" size={19} />
                  ) : (
                    <BadgeCheck size={19} />
                  )}
                  {busy ? "Verifying account…" : "Verify & open workspace"}
                </button>
                <div className="px-site-login-secondary-actions">
                  <button type="button" onClick={resetEmail}>
                    Use another email
                  </button>
                  <button
                    type="button"
                    onClick={() => sendCode(undefined, true)}
                    disabled={busy || resendIn > 0}
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </button>
                </div>
              </form>
            )}

            {notice && (
              <div className="px-site-login-notice" role="status">
                <BadgeCheck size={18} />
                <span>{notice}</span>
              </div>
            )}
            {displayedError && (
              <div
                className="px-site-login-notice px-site-login-notice--error"
                role="alert"
                data-state={displayedError.kind}
              >
                <ShieldCheck size={18} />
                <span>
                  <strong>{displayedError.title}</strong>
                  <small>{displayedError.message}</small>
                  {displayedError.kind === "admin-only" && (
                    <a href={getPanelUrls().admin}>
                      Open Admin sign-in <ArrowUpRight size={13} />
                    </a>
                  )}
                </span>
              </div>
            )}
            <WorkspaceChoices result={routeResult} />

            {!awaitingCode && (
              <div className="px-site-newcomer" aria-label="New to PhotoX">
                <strong>New to PhotoX?</strong>
                <p>
                  No password or separate account setup is needed. Your email
                  verification creates your account; the next step depends on
                  what you need.
                </p>
                <div className="px-site-newcomer-actions">
                  <a href="/find-photographer">
                    <Camera size={16} /> Request a photographer{" "}
                    <ArrowUpRight size={14} />
                  </a>
                  <a href={photographerApplicationHref()}>
                    <Users size={16} /> Apply as a photographer{" "}
                    <ArrowUpRight size={14} />
                  </a>
                </div>
                <small>
                  Event guests should scan their event QR link instead of
                  registering as Clients or Photographers.
                </small>
              </div>
            )}

            <div className="px-site-login-divider">
              <span /> VERIFIED ACCOUNT ROUTING <span />
            </div>
            <div className="px-site-login-role-note">
              <div className="px-site-login-role-icons">
                <Camera size={17} />
                <Users size={17} />
                <Images size={17} />
              </div>
              <p>
                <strong>No role claim is trusted from this page.</strong>
                <br />
                After OTP verification, the backend account decides which
                workspaces are available. Admin access remains separate.
              </p>
            </div>
            <div
              className="px-site-intent-switch"
              aria-label="Choose another PhotoX entrance"
            >
              <span>Another entrance?</span>
              <a
                className={intent === "client" ? "is-active" : ""}
                href={loginHref("client")}
              >
                Client
              </a>
              <a
                className={intent === "photographer" ? "is-active" : ""}
                href={loginHref("photographer")}
              >
                Photographer
              </a>
              <a
                className={intent === "guest" ? "is-active" : ""}
                href={loginHref("guest")}
              >
                Guest
              </a>
            </div>
            <button
              type="button"
              className="px-site-help-toggle"
              onClick={() => setShowHelp(!showHelp)}
              aria-expanded={showHelp}
            >
              How does sign-in work? <ChevronDown size={17} />
            </button>
            {showHelp && (
              <p className="px-site-login-help">
                PhotoX sends a short-lived code to your email. A verified Client
                opens the Client panel, a Photographer opens the professional
                workspace, and a Guest can revisit authorized galleries and face
                search. Event QR links continue directly to their event.
              </p>
            )}
            <p className="px-site-login-legal">
              By continuing, you confirm that you are authorized to use this
              email. Event and photo access remain enforced by the PhotoX
              backend.
            </p>
          </div>
          <div className="px-site-login-bottom">
            Have an event QR?{" "}
            <span>Scan it to open the correct event automatically.</span>{" "}
            <a href={loginHref("guest")}>
              Returning Guest access <ArrowUpRight size={15} />
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
